<template>
  <div>
    <div v-if="!loading">
      <cl-sidebar-dropdown
        :is-closed="isClosed"
        :test-id="dataTestId"
        @on-dropdown="resetList"
        @scroll-end="emit('loadMore')"
      >
        <template #title>
          <div class="tw-flex tw-items-center tw-gap-4">
            <div
              class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#219ad3] tw-p-1"
            >
              <component :is="icon" />
            </div>
            <div
              v-if="!isClosed"
              class="tw-max-w-40 tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis"
            >
              {{ selected ? selected.name : "" }}
            </div>
          </div>
        </template>
        <template #search>
          <cl-sidebar-dropdown-search @search="debounceFilter" />
        </template>
        <template #menu>
          <template v-if="items.length > 0">
            <cl-sidebar-dropdown-menu-item
              v-for="(item, index) in items"
              :key="item.name"
              :data-test-id="`${dataTestId}-${index}`"
              class="tw-cursor-pointer tw-py-4 tw-max-w-sm tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis"
              @on-click="setSelected(item)"
            >
              {{ item.name }}
            </cl-sidebar-dropdown-menu-item>
          </template>
          <li v-if="loadingMore" class="tw-flex tw-justify-center tw-py-3">
            <cl-spinner :size="'small'" />
          </li>
          <template v-else-if="items.length === 0">
            <li class="tw-px-6 tw-py-4">{{ $t("No results found") }}</li>
          </template>
        </template>
      </cl-sidebar-dropdown>
    </div>
    <cl-skeleton v-else>
      <cl-skeleton-row />
      <cl-skeleton-row />
    </cl-skeleton>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, toRefs, computed } from "vue";
import { refDebounced, useDebounceFn } from "@vueuse/core";

type DropdownItem = {
  id: string | number;
  name: string;
};

type Props = {
  isClosed: boolean;
  options: DropdownItem[];
  initialSelected?: string | number;
  dataTestId: string;
  loading?: boolean;
  loadingMore?: boolean;
  icon?: string;
};

type Emits = {
  (e: "selected", value: DropdownItem): void;
  (e: "search", value: string): void;
  (e: "resetList"): void;
  (e: "loadMore"): void;
};

const props = withDefaults(defineProps<Props>(), {
  initialSelected: "",
  loading: false,
  loadingMore: false,
  icon: "cl-icon-tabler-icon-building-skyscraper",
});

const emit = defineEmits<Emits>();

const { options } = toRefs(props);
const items = computed(() => options.value);

const debounceFilter = useDebounceFn((searchString: string) => {
  emit("search", searchString);
}, 500);

const search = ref<string>("");
const searchDebounced = refDebounced(search, 300);
const selected = ref<DropdownItem | null>(null);

watch(
  () => props.options,
  (newOptions: DropdownItem[]) => {
    selected.value = props.initialSelected
      ? newOptions.find((item) => item.id == props.initialSelected) || null
      : newOptions[0] || null;
  },
  { deep: true, immediate: true },
);

watch(searchDebounced, (newSearch: string) => {
  emit("search", newSearch);
});

const resetList = (): void => {
  emit("resetList");
};

const setSelected = (selectedOption: DropdownItem): void => {
  selected.value = selectedOption;
  emit("selected", selected.value);
};
</script>
